/**
 * TaskDrawerContent Component
 *
 * Renders the main content area with:
 * - Comments tab with task description + user comments
 * - Campaigns tab (conditional)
 * - History tab with activity timeline
 *
 * Accessibility:
 * - ARIA tabpanel structure
 * - Focus management on tab change
 * - Lazy loading for performance
 *
 * @see WCAG 2.1 AA - 2.4.3 Focus Order
 */

import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { Tabs } from 'antd';
import { TaskComments } from '../TaskComments';
import { Histories } from '../../../../shared/Histories';
import TaskDrawerCampaigns from './TaskDrawerCampaigns';
import { hasCampaignsFeature } from '../../utils/task.utils';
import '../../styles/index.scss';

const { TabPane } = Tabs;

const TaskDrawerContent = ({
  currentTask,
  taskId,
  currentUser,
  activeTab,
  onTabChange,
  commentFormRef,
  onTaskUpdate,
  // History data
  histories,
  loadingHistories,
  fetchHistories,
  historiesFetched,
  setHistoriesFetched,
}) => {
  const showCampaignsTab =
    currentTask?.relatedCampaigns?.length > 0 &&
    hasCampaignsFeature(currentUser);

  /**
   * Handle tab change with lazy loading for history
   */
  const handleTabChange = useCallback(
    (key) => {
      onTabChange(key);

      // Lazy load histories on first visit to History tab
      if (key === '3' && taskId && !historiesFetched) {
        fetchHistories({
          variables: {
            where: {
              referenceId: taskId,
              type: 'TASK',
            },
          },
        });
        setHistoriesFetched(true);
      }
    },
    [taskId, historiesFetched, fetchHistories, setHistoriesFetched, onTabChange]
  );

  // Get comment count (only user comments, not the initial task description)
  const commentCount = currentTask?.attachedComments?.length || 0;
  const historyCount = histories?.length || 0;

  return (
    <div
      className="task-drawer-full__main"
      role="region"
      aria-label="Task details"
    >
      <Tabs
        activeKey={activeTab}
        onChange={handleTabChange}
        type="card"
        className="task-tabs-details-history"
        aria-label="Task information tabs"
      >
        {/* Comments Tab */}
        <TabPane
          tab={`Comments (${commentCount})`}
          key="1"
          role="tabpanel"
          aria-labelledby="tab-comments"
        >
          <div className="task-drawer-full__tab-content">
            <TaskComments
              currentTask={currentTask}
              currentTaskId={taskId}
              formRef={commentFormRef}
              onTaskUpdate={onTaskUpdate}
            />
          </div>
        </TabPane>

        {/* Campaigns Tab (conditional) */}
        {showCampaignsTab && (
          <TabPane
            tab="Campaigns"
            key="2"
            role="tabpanel"
            aria-labelledby="tab-campaigns"
          >
            <TaskDrawerCampaigns campaigns={currentTask.relatedCampaigns} />
          </TabPane>
        )}

        {/* History Tab */}
        <TabPane
          tab={`History (${historyCount})`}
          key="3"
          role="tabpanel"
          aria-labelledby="tab-history"
        >
          <div className="task-drawer-full__tab-content">
            <Histories histories={histories} loading={loadingHistories} />
          </div>
        </TabPane>
      </Tabs>
    </div>
  );
};

TaskDrawerContent.propTypes = {
  currentTask: PropTypes.shape({
    id: PropTypes.string,
    relatedCampaigns: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        status: PropTypes.string,
        startDate: PropTypes.string,
        endDate: PropTypes.string,
      })
    ),
  }),
  taskId: PropTypes.string.isRequired,
  currentUser: PropTypes.shape({
    id: PropTypes.string,
    role: PropTypes.shape({
      roleItems: PropTypes.array,
    }),
  }),
  activeTab: PropTypes.string,
  onTabChange: PropTypes.func.isRequired,
  commentFormRef: PropTypes.object,
  onTaskUpdate: PropTypes.func,
  histories: PropTypes.array,
  loadingHistories: PropTypes.bool,
  fetchHistories: PropTypes.func.isRequired,
  historiesFetched: PropTypes.bool,
  setHistoriesFetched: PropTypes.func.isRequired,
};

TaskDrawerContent.defaultProps = {
  currentTask: null,
  currentUser: null,
  activeTab: '1',
  commentFormRef: null,
  onTaskUpdate: null,
  histories: [],
  loadingHistories: false,
  historiesFetched: false,
};

export default TaskDrawerContent;
